<template>
  <!--弹框Terms and use 开始-->
  <el-dialog :visible.sync="framePointsDia" width="65%" :show-close="termsAndConditionsShowClose" class="tax-margin" top="10vh">
    <div class="container">

      <header>
        <h1>Hibeds.com Booking Rewards Program</h1>
        <p>Official points system for hotel reservations</p>
      </header>

      <section class="formula-section">
        <h2>Points Accumulation Rules</h2>

        <p>Thank you for using our Points Accumulation System! Through your spending, not only can you enjoy high-quality hotel services, but you will also accumulate points to redeem more exclusive offers. Please read the following points accumulation rules in detail:</p>

        <h3>I. Points Tiers</h3>
        <p>Based on your total spending in the current quarter, you will automatically enter the corresponding points tier and enjoy different points accumulation rates:</p>

        <ul>
          <li>Tier 1: Accumulate 20% of your order payment amount as points if your cumulative spending reaches 50,000 USD in the current quarter.</li>
          <li>Tier 2: Accumulate 50% of your order payment amount as points if your cumulative spending reaches 100,000 USD in the current quarter.</li>
          <li>Tier 3: Accumulate 100% of your order payment amount as points if your cumulative spending reaches 200,000 USD in the current quarter.</li>
        </ul>

        <h3>II. Points Redemption Ratio</h3>
        <p>Points can be redeemed at a ratio of 100 points = 1 USD (other currencies will be calculated based on the real-time exchange rate). You can use your points for redemption at any time.</p>

        <h2>Points Calculation Formulas</h2>

        <div class="note">
          <div class="note-title">Important Note:</div>
          <p>All calculations are based on the <strong>Actual Paid Revenue</strong> (the actual payment amount for the order), not the listed price or any pre-discount amounts.</p>
        </div>

        <h3>Basic Points Calculation</h3>
        <div class="formula">
          <div class="formula-title">Points Calculation for a Single Stay</div>
          <p>Points = Actual Paid Revenue × Tier Percentage</p>
          <div class="formula-desc">
            This formula calculates the base points earned for a single completed stay.
          </div>
          <div class="variables">
            <div class="variable"><span class="variable-name">Actual Paid Revenue:</span> The final amount paid by the customer after all discounts and promotions</div>
            <div class="variable"><span class="variable-name">Tier Percentage:</span> The points earning percentage based on the member's tier at the time of check-in (e.g., 20% = 0.2)</div>
          </div>
        </div>

        <div class="example">
          <div class="example-title">Example:</div>
          <p>If a member with Gold tier (20% points) completes a stay with Actual Paid Revenue of $500:</p>
          <p>Points = $500 × 0.20 = 100 points</p>
        </div>

        <h3>Quarterly Points Calculation</h3>
        <div class="formula">
          <div class="formula-title">Final Points Calculation for a Quarter</div>
          <p>Final Points = Σ(Each Stay's Actual Paid Revenue × Final Tier Percentage)</p>
          <div class="formula-desc">
            This formula calculates the total points earned for all stays in a quarter, using the member's final tier percentage for that quarter.
          </div>
        </div>

        <h3>Points Adjustment Calculation</h3>
        <div class="formula">
          <div class="formula-title">Points Adjustment After Tier Change</div>
          <p>Adjustment = Σ(All Stays' Actual Paid Revenue × New Tier Percentage) - Previously Credited Points</p>
          <div class="formula-desc">
            This formula calculates the points adjustment needed when a member's tier changes during or after the quarter.
          </div>
        </div>

        <div class="example">
          <div class="example-title">Example:</div>
          <p>A member had 3 stays totaling $1,500 in Actual Paid Revenue and was initially credited 150 points (Silver tier at 10%). After tier review, they qualified for Gold tier (20%):</p>
          <p>Adjustment = ($1,500 × 0.20) - 150 = 300 - 150 = 150 additional points</p>
        </div>
      </section>

      <section class="faq-section">
        <h2>Frequently Asked Questions</h2>

        <div class="faq-item">
          <div class="question">Q: When will I see adjustments from tier changes?</div>
          <div class="answer">
            <p>A: The tier evaluation process works as follows:</p>
            <ol>
              <li>Your tier for the current quarter is updated the day after each check-in, based on your cumulative stays and spending up to that point.</li>
              <li>At the end of each quarter (March 31, June 30, September 30, December 31), we perform a final tier review.</li>
              <li>Any points adjustments resulting from tier changes are calculated and processed within 7 business days after the quarter ends.</li>
              <li>The adjusted points will appear in your account by the 7th business day of the new quarter.</li>
            </ol>
            <p>For example, if your tier changes from Silver to Gold after the Q2 review on June 30, any additional points will be credited by July 7.</p>
          </div>
        </div>

        <div class="faq-item">
          <div class="question">Q: How are points calculated for bookings in future quarters?</div>
          <div class="answer">
            <p>A: Points for future bookings are calculated based on these principles:</p>
            <ul>
              <li>Each quarter's points calculation is completely independent of other quarters.</li>
              <li>The tier level used for points calculation is determined by your status <strong>at the time of check-in</strong>, not at the time of booking.</li>
              <li>The "Pending Points" shown for future stays are estimates based on your <em>current</em> tier level.</li>
              <li>If your tier changes before check-in, the actual points earned will be calculated using your new tier percentage.</li>
            </ul>
            <p>For example, if you book a stay in Q3 while you're a Silver member (10%), but achieve Gold status (20%) before the actual check-in date, you'll earn points at the Gold tier rate.</p>
          </div>
        </div>

        <div class="faq-item">
          <div class="question">Q: Can I see how my points were calculated?</div>
          <div class="answer">
            <p>A: Yes, we provide full transparency into your points calculations through multiple channels:</p>
            <ul>
              <li><strong>Transaction History:</strong> Each completed stay shows:
                <ul>
                  <li>Actual Paid Revenue amount</li>
                  <li>Tier percentage applied</li>
                  <li>Points calculation (Revenue × Tier Percentage)</li>
                  <li>Date of credit</li>
                </ul>
              </li>
              <li><strong>Quarterly Statements:</strong> Available in your account by the 7th business day of each new quarter, showing:
                <ul>
                  <li>All stays from the previous quarter</li>
                  <li>Initial points credited</li>
                  <li>Any adjustments from tier changes</li>
                  <li>Final points total</li>
                </ul>
              </li>
              <li><strong>Exportable Reports:</strong> You can download detailed CSV reports that include:
                <ul>
                  <li>Stay dates and properties</li>
                  <li>Payment details</li>
                  <li>Points calculation breakdown</li>
                  <li>Tier status at time of check-in</li>
                </ul>
              </li>
            </ul>
            <p>Any points adjustments from tier changes will be clearly marked in your transaction history with the notation "Tier Adjustment" and the calculation details.</p>
          </div>
        </div>
        <div class="faq-item-bottom">
          <div class="question">Q: What happens if I cancel or modify a booking?</div>
          <div class="answer">
            <p>A: Our cancellation and modification policy affects points as follows:</p>
            <ul>
              <li><strong>Cancellations:</strong>
                <ul>
                  <li>If canceled before check-in: No points are earned</li>
                  <li>If canceled after check-in: Points are calculated based on actual paid amount for the nights stayed</li>
                </ul>
              </li>
              <li><strong>Modifications:</strong>
                <ul>
                  <li>If the modification changes the Actual Paid Revenue (e.g., adding nights or services), points are recalculated</li>
                  <li>If the modification doesn't affect payment (e.g., room type change at same rate), points remain unchanged</li>
                </ul>
              </li>
              <li><strong>Early Check-outs:</strong> Points are calculated based on actual nights stayed and paid for</li>
            </ul>
            <p>All adjustments from cancellations or modifications appear in your transaction history within 24 hours of the change.</p>
          </div>
        </div>
      </section>
<footer>
  <p>Document version: 2.1 | Updated: 2024-03-15</p>
  <p>© 2024 Hibeds.com</p>
</footer>
 </div>
    <div @click="framePointsDia = false" style="border-top: 1px solid rgba(221, 223, 226, 1);font-weight: 100; font-style: normal; font-size: 16px; color: #5392F9; text-align: center; height: 60px;margin: 20px -5px -30px -5px;">
      <el-button tabindex="3" style="margin: 30px 0 -20px 0;">Close</el-button>
    </div>
  </el-dialog>
  <!--弹框1 结束-->
  <!--引入方式-->
  <!-- <framePoints :framePointsDialog.sync="framePointsDialog"></framePoints> -->
</template>

<script>
export default {
  name: 'framePoints',
  props: {
    framePointsDialog: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      frameWidth: '450px',
      showClo: false,
      termsAndConditionsShowClose: true
    }
  },
  watch: {
    framePointsDia (val) {
      if (!val) { // 当弹框关闭时
        localStorage.setItem('firstLogin', false)
      }
    }
  },
  computed: {
    framePointsDia: {
      get () {
        return this.framePointsDialog
      },
      set (val) {
        this.$emit('update:framePointsDialog', val)
      }
    }
  },
  mounted () {
    if (document.body.clientWidth < 1001) {
      this.frameWidth = '95%'
    } else {
      this.frameWidth = '450px'
    }
  }
}
</script>

<style scoped>
/deep/ .frameTermAndUseDialogDiv{background-color: unset !important; box-shadow: 0px 0px 0px white;}
/deep/ .el-button, .register_send, /deep/ .el-button:focus, .register_send:focus{width: 140px;height: 40px; font-size:14px; background-color: rgba(83, 102, 163, 1);border: 1px solid rgba(83, 102, 163, 1);border-radius: 5px;color: #FFFFFF;}
/deep/ .el-button:focus, .el-button:hover{width: 140px;height: 40px; font-size:14px;background-color: #FFF; border: 1px solid rgba(83, 102, 163, 1); color:rgba(83, 102, 163, 1);}

/*控制弹窗高度*/
/deep/ .el-dialog {
  height: 85vh !important;
}

/deep/ .el-dialog__body {
  height: calc(85vh - 130px) !important;
  overflow-y: auto !important;
}

/*内容样式*/
body {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  line-height: 1.6;
  margin: 0;
  padding: 0;
  color: #333333;
  background-color: #f5f7fa;
}

.container {
  padding: 0px 20px;
}

header {
  border-bottom: 1px solid #C5C9D6;
  padding-bottom: 20px;
  margin-bottom: 30px;
}

h1 {
  color: #2A3050;
  margin-bottom: 10px;
}

h2 {
  color: #3A4B7C;
  margin-top: 30px;
  padding-bottom: 10px;
  border-bottom: 1px solid #C5C9D6;
}

h3 {
  color: #5566A1;
  margin-top: 25px;
}

.formula-section {
  margin-bottom: 40px;
}

.formula {
  background-color: #F5F6FA;
  padding: 20px;
  border-left: 5px solid #5566A1;
  margin: 20px 0;
  font-family: 'Courier New', Courier, monospace;
  border-radius: 0 5px 5px 0;
  overflow-x: auto;
}

.formula-title {
  font-weight: bold;
  margin-bottom: 10px;
  color: #2A3050;
}

.formula-desc {
  margin-top: 10px;
  font-size: 0.95em;
  color: #555;
}

.variables {
  margin-top: 15px;
  font-size: 0.9em;
}

.variable {
  margin-bottom: 5px;
}

.variable-name {
  font-weight: bold;
  color: #3A4B7C;
}

.faq-section {
  margin-top: 50px;
}

.faq-item {
  margin-bottom: 30px;
  border-bottom: 1px dashed #C5C9D6;
  padding-bottom: 20px;
}

.faq-item-bottom {
  margin-bottom: 30px;
}

.question {
  font-weight: bold;
  color: #3A4B7C;
  font-size: 1.1em;
  margin-bottom: 10px;
}

.answer {
  line-height: 1.7;
}

.answer ul li ul {
  padding-left: 20px;  /* 添加二级列表的缩进 */
  margin: 5px 0;
}

.answer ul li ul li {
  margin: 5px 0;
}

.note {
  background-color: #fffde7;
  padding: 15px;
  border-left: 5px solid #D4A55E;
  margin: 20px 0;
  font-size: 0.9em;
}

.note-title {
  font-weight: bold;
  margin-bottom: 5px;
  color: #D4A55E;
}

.example {
  background-color: #e8f5e9;
  padding: 15px;
  border-left: 5px solid #6DAB6F;
  margin: 20px 0;
}

.example-title {
  font-weight: bold;
  margin-bottom: 5px;
  color: #6DAB6F;
}

footer {
  margin-top: 50px;
  text-align: center;
  font-size: 0.9em;
  color: #7f8c8d;
  border-top: 1px solid #C5C9D6;
  padding-top: 20px;
}

@media (max-width: 768px) {
  .container {
    padding: 15px;
    margin-top: 15px;
    margin-bottom: 15px;
  }

  h1 {
    font-size: 1.8em;
  }

  .formula {
    padding: 15px;
    font-size: 0.9em;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/public/framePoints.vue